import { ReactNode, useState } from "react"

export type TelegramParseMode = "Markdown" | "MarkdownV2"

/**
 * Renders Telegram Markdown or MarkdownV2 text as a React node.
 * Implements the Telegram Bot API spec exactly:
 *   https://core.telegram.org/bots/api#formatting-options
 *
 * Key differences from CommonMark that this renderer handles correctly:
 *   *text*  = bold   (CommonMark: italic)
 *   **text** = literal asterisks (CommonMark: bold) — not valid in Telegram
 *   _text_  = italic (same in both)
 *
 * Mode isolation:
 *   "Markdown" supports only: *bold* _italic_ `code` ```pre``` [link](url)
 *   "MarkdownV2" adds: __underline__ ~strike~ ||spoiler|| >blockquote and more
 */
export function renderMarkdown(
  text: string,
  mode: TelegramParseMode,
): ReactNode {
  return <>{blockParse(text, mode)}</>
}

// ─── Block parser ─────────────────────────────────────────────────────────────
// Handles: ``` pre blocks (both modes), > blockquotes (MDv2 only)

function blockParse(text: string, mode: TelegramParseMode): ReactNode[] {
  const result: ReactNode[] = []
  let key = 0

  // Split by ``` fences first — they're verbatim and must not be inline-parsed
  for (const part of splitFences(text)) {
    if (part.kind === "fence") {
      result.push(
        <pre key={key++}>
          <code className={part.lang ? `language-${part.lang}` : undefined}>
            {part.code}
          </code>
        </pre>,
      )
    } else if (mode === "MarkdownV2") {
      // MDv2: detect blockquotes line by line within non-fence text
      for (const node of blockquoteParse(part.value, mode, key)) {
        result.push(node)
        key++
      }
    } else {
      for (const node of inlineParse(part.value, mode, key)) {
        result.push(node)
        key++
      }
    }
  }

  return result
}

// ─── Fence splitter ───────────────────────────────────────────────────────────

type FencePart =
  | { kind: "fence"; lang: string; code: string }
  | { kind: "text"; value: string }

function splitFences(text: string): FencePart[] {
  const parts: FencePart[] = []
  // Match ```lang\n...code...\n``` (lang is optional, code may be empty)
  const re = /```([^\n`]*)\n([\s\S]*?)```/g
  let last = 0

  for (const m of text.matchAll(re)) {
    if (m.index! > last) {
      parts.push({ kind: "text", value: text.slice(last, m.index) })
    }
    parts.push({ kind: "fence", lang: m[1].trim(), code: m[2] })
    last = m.index! + m[0].length
  }

  if (last < text.length) {
    parts.push({ kind: "text", value: text.slice(last) })
  }

  return parts
}

// ─── Blockquote processor (MDv2 only) ────────────────────────────────────────
// Processes a text segment line by line; lines starting with > or **> become
// blockquotes; all other lines are accumulated and passed to inlineParse.

function blockquoteParse(
  text: string,
  mode: TelegramParseMode,
  keyStart: number,
): ReactNode[] {
  const result: ReactNode[] = []
  const lines = text.split("\n")
  let i = 0
  let key = keyStart
  let inlineBuf = ""

  const flushInline = () => {
    if (!inlineBuf) return
    for (const node of inlineParse(inlineBuf, mode, key)) {
      result.push(node)
      key++
    }
    inlineBuf = ""
  }

  while (i < lines.length) {
    const line = lines[i]

    // Expandable blockquote: line starts with **>
    if (line.startsWith("**>")) {
      flushInline()
      const bqLines: string[] = [line.slice(3)]
      i++
      while (i < lines.length && lines[i].startsWith(">")) {
        const l = lines[i].slice(1)
        if (l.endsWith("||")) {
          bqLines.push(l.slice(0, -2))
          i++
          break
        }
        bqLines.push(l)
        i++
      }
      const inner = inlineParse(bqLines.join("\n"), mode, 0)
      result.push(
        <blockquote key={key++} className="tg-expandable">
          {inner}
        </blockquote>,
      )
      continue
    }

    // Regular blockquote: line starts with >
    if (line.startsWith(">")) {
      flushInline()
      const bqLines: string[] = [line.slice(1).replace(/^ /, "")]
      i++
      while (i < lines.length && lines[i].startsWith(">")) {
        bqLines.push(lines[i].slice(1).replace(/^ /, ""))
        i++
      }
      const inner = inlineParse(bqLines.join("\n"), mode, 0)
      result.push(<blockquote key={key++}>{inner}</blockquote>)
      continue
    }

    // Regular line — accumulate for inline parsing
    inlineBuf += (inlineBuf ? "\n" : "") + line
    i++
  }

  flushInline()
  return result
}

// ─── Inline parser ────────────────────────────────────────────────────────────

interface Ctx {
  text: string
  pos: number
  mode: TelegramParseMode
}

function inlineParse(
  text: string,
  mode: TelegramParseMode,
  keyStart: number,
): ReactNode[] {
  const ctx: Ctx = { text, pos: 0, mode }
  const nodes: ReactNode[] = []
  let buf = ""
  let key = keyStart

  const flush = () => {
    if (buf) {
      nodes.push(buf)
      buf = ""
    }
  }

  while (ctx.pos < ctx.text.length) {
    const node = nextNode(ctx, key)
    if (node !== null) {
      flush()
      nodes.push(node)
      key++
    } else {
      buf += ctx.text[ctx.pos++]
    }
  }

  flush()
  return nodes
}

function nextNode(ctx: Ctx, key: number): ReactNode | null {
  const { text, pos, mode } = ctx
  const ch = text[pos]

  // ── Escape ────────────────────────────────────────────────────────────────
  if (ch === "\\") {
    const next = text[pos + 1]
    if (!next) return null
    const escSet = mode === "MarkdownV2" ? "_*[]()~`>#+\\-=|{}.!" : "_*`["
    if (escSet.includes(next)) {
      ctx.pos += 2
      // Return the literal char as a string — `string` is assignable to ReactNode;
      // non-null signals "consumed" to the caller, preventing pos double-advance.
      return next
    }
  }

  // ── Inline code: `...` ────────────────────────────────────────────────────
  if (ch === "`") {
    const end = text.indexOf("`", pos + 1)
    if (end !== -1) {
      ctx.pos = end + 1
      return <code key={key}>{text.slice(pos + 1, end)}</code>
    }
  }

  // ── MDv2: tg:// image links (custom emoji, datetime) ──────────────────────
  if (mode === "MarkdownV2" && ch === "!") {
    const m = text
      .slice(pos)
      .match(/^!\[([^\]]*)\]\(tg:\/\/(?:emoji|time)\?[^)]*\)/)
    if (m) {
      ctx.pos += m[0].length
      // Return alt/label text (may be "" for emoji with no alt).
      // Must return a non-null value so the caller does NOT double-advance pos.
      // Empty string is a valid ReactNode that renders nothing.
      return m[1]
    }
  }

  // ── Link: [text](url) ──────────────────────────────────────────────────
  if (ch === "[") {
    const closeBracket = text.indexOf("]", pos + 1)
    if (closeBracket !== -1 && text[closeBracket + 1] === "(") {
      const closeParens = text.indexOf(")", closeBracket + 2)
      if (closeParens !== -1) {
        ctx.pos = closeParens + 1
        return (
          <a
            key={key}
            href={text.slice(closeBracket + 2, closeParens)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.slice(pos + 1, closeBracket)}
          </a>
        )
      }
    }
  }

  // ── MDv2-only inline markup ───────────────────────────────────────────────

  if (mode === "MarkdownV2") {
    // Spoiler: ||...||
    if (text.startsWith("||", pos)) {
      const end = text.indexOf("||", pos + 2)
      if (end !== -1) {
        const inner = inlineParse(text.slice(pos + 2, end), mode, 0)
        ctx.pos = end + 2
        return <TgSpoiler key={key}>{inner}</TgSpoiler>
      }
    }

    // Underline: __...__ (check before italic _ to prevent ambiguity)
    if (text.startsWith("__", pos)) {
      const end = text.indexOf("__", pos + 2)
      if (end !== -1) {
        const inner = inlineParse(text.slice(pos + 2, end), mode, 0)
        ctx.pos = end + 2
        return <u key={key}>{inner}</u>
      }
    }

    // Strikethrough: ~...~ (single tilde only, not ~~)
    if (ch === "~" && text[pos + 1] !== "~") {
      const end = findSingle(text, "~", pos + 1)
      if (end !== -1) {
        const inner = inlineParse(text.slice(pos + 1, end), mode, 0)
        ctx.pos = end + 1
        return <s key={key}>{inner}</s>
      }
    }
  }

  // ── Bold: *...* (single asterisk only — both modes) ───────────────────────
  // text[pos+1] !== '*' ensures we don't match ** which is not Telegram markup
  if (ch === "*" && text[pos + 1] !== "*") {
    const end = findSingle(text, "*", pos + 1)
    if (end !== -1) {
      const inner = inlineParse(text.slice(pos + 1, end), mode, 0)
      ctx.pos = end + 1
      return <strong key={key}>{inner}</strong>
    }
  }

  // ── Italic: _..._ (single underscore only — both modes) ──────────────────
  // !text.startsWith("__", pos) ensures we don't try to match the start of __
  if (ch === "_" && !text.startsWith("__", pos)) {
    const end = findSingle(text, "_", pos + 1)
    if (end !== -1) {
      const inner = inlineParse(text.slice(pos + 1, end), mode, 0)
      ctx.pos = end + 1
      return <em key={key}>{inner}</em>
    }
  }

  return null
}

/**
 * Finds the next single occurrence of `delim` starting at `from`,
 * skipping positions where the delimiter is adjacent to another identical
 * delimiter (i.e., ** or __ would not match as bold/italic).
 */
function findSingle(text: string, delim: string, from: number): number {
  for (let i = from; i < text.length; i++) {
    if (
      text[i] === delim &&
      text[i - 1] !== "\\" && // not escaped
      text[i - 1] !== delim && // not a double delimiter on the left
      text[i + 1] !== delim // not a double delimiter on the right
    ) {
      return i
    }
  }
  return -1
}

// ─── TgSpoiler component ──────────────────────────────────────────────────────

function TgSpoiler({ children }: { children: ReactNode }) {
  const [revealed, setRevealed] = useState(false)
  return (
    <span
      className={`tg-spoiler${revealed ? " tg-spoiler--revealed" : ""}`}
      onClick={() => setRevealed(true)}
    >
      {children}
    </span>
  )
}
